import React from 'react';

import { DialogActions, DialogContent, Typography } from '@mui/material';
import { gridFilterModelSelector, useGridSelector } from '@mui/x-data-grid-premium';
import { AlineButton, AlineDialog } from 'src/components';
import { useStable } from 'src/editor/helpers/useStable';
import { useReportParams } from 'src/helpers';
import { useCancelExtractionRuns, useGetSelectedReportRows } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  dialogPaper: {
    width: 640,
  },
  submitButton: {
    marginLeft: theme.spacing(1),
  },
}));

interface StopAIDialogProps {
  onClose: () => void;
  open: boolean;
  rowCount: number;
}

/**
 * Confirms stopping every queued or running question on report documents:
 * the selected rows, or every row matching the live filter. Same scope as
 * `RunAIDialog`, so Stop ends what Run started.
 */
export function StopAIDialog({ onClose, open, rowCount }: StopAIDialogProps): JSX.Element {
  const { classes } = useStyles();
  const { reportId } = useReportParams();
  const { apiRef, selectedReportRows } = useGetSelectedReportRows();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { cancelExtractionRuns, loading } = useCancelExtractionRuns();
  const documentCount = selectedReportRows.length || rowCount;

  async function handleStop(): Promise<void> {
    if (selectedReportRows.length)
      await cancelExtractionRuns(
        { documentIds: selectedReportRows.map(row => row.documentId) },
        { showResult: true }
      );
    else
      await cancelExtractionRuns(
        { documentIds: [], filterModel, filterTimeZone, reportId, runAllDocs: true },
        { showResult: true }
      );
    onClose();
  }

  return (
    <AlineDialog
      classes={{ paper: classes.dialogPaper }}
      open={open}
      title="Stop Aline AI?"
      onClose={onClose}
    >
      <DialogContent>
        <Typography>{getStopConfirmation(documentCount)}</Typography>
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <div className={classes.submitButton}>
          <AlineButton color="primary" loading={loading} variant="contained" onClick={handleStop}>
            Stop
          </AlineButton>
        </div>
      </DialogActions>
    </AlineDialog>
  );
}

export function getStopConfirmation(documentCount: number): string {
  const documents = `${documentCount} ${documentCount === 1 ? 'document' : 'documents'}`;
  return `Stop running questions for ${documents}? Answers that already finished are kept.`;
}
